import { Component, Mixins, Watch } from 'vue-property-decorator'
import IconsMixin from '@/mixins/IconsMixin.vue'
import AuthMixin from '@/mixins/AuthMixin.vue'
import LabeledValue from '@/components/LabeledValue.vue'
import {
  Company,
  CompanyStatusEnum,
  CompanyTypeEnum,
  IVendorPriceListEntry,
  MasterCatalogCollection,
  Order,
  OrderCollection,
  OrderEntry, OrderEntryStatusEnum,
  OrderStatusEnum,
  OrderTypeEnum,
  PriceList,
  PriceListCollection,
  PriceListEntry,
  ReminderCollection
} from '../../../firestore'
import LoadingPage from '@/components/LoadingPage.vue'
import moment from 'moment'
import ProductSelector from '@/components/Order/ProductSelector.vue'
import QuantityTypeSelector from '@/components/Order/QuantityTypeSelector.vue'
import OrderBarcodeScanDialog from '@/components/Barcode/OrderBarcodeScanDialog.vue'
import SaveButton from '@/components/SaveButton.vue'
import PriceListSelector from '@/components/Order/PriceListSelector.vue'
import CategorySelectionModal from '@/components/Order/CategorySelectionModal.vue'
import FavoriteListSelectionModal from '@/components/Order/FavoriteListSelectionModal.vue'
import AddVendorModal from '@/components/Onboarding/AddVendorModal.vue'
import store, { Actions } from '../../../store'
import ConfirmationModal from '@/components/ConfirmationModal.vue'
import QuantityComboBox from '@/components/QuantityComboBox.vue'
import { v4 as uuid } from 'uuid'
import * as Sentry from '@sentry/vue'
import { Severity } from '@sentry/browser'

@Component({
  components: {
    PriceListSelector,
    SaveButton,
    OrderBarcodeScanDialog,
    ProductSelector,
    LoadingPage,
    LabeledValue,
    CategorySelectionModal,
    FavoriteListSelectionModal,
    AddVendorModal,
    QuantityTypeSelector,
    ConfirmationModal,
    QuantityComboBox
  }
})
export default class OrderEdit extends Mixins(IconsMixin, AuthMixin) {
  public alert: Boolean = false
  public order: Order | null = null
  public selectedEntry: OrderEntry = {} as OrderEntry
  public action: 'create' | 'modify' | '' = ''
  public isActive: boolean = true

  public displayBarcodeScanner: boolean = false
  public errors: string[] = []
  public showAddVendorModal = false
  public newVendor: {
    name: string,
    phoneNumber: string,
    email: string
  } = {
    name: '',
    phoneNumber: '',
    email: ''
  }
  public items = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15]
  public vendors: Company[] = []
  public priceListEntries: IVendorPriceListEntry[] = []
  public priceLists: PriceList[] = []

  public reminderMap: { [entryIdAndVendorId: string]: string[] } = {['']: ['']}

  public reminders: Map<string, string> = new Map()

  public isSaving: boolean = false
  public shouldSaveDraft: boolean = true

  public $refs!: {
    form: HTMLFormElement
  }

  get deliveryDate(): string | undefined {
    if (this.order) {
      return moment(this.order.dateExpected).local().format('YYYY-MM-DD')
    }
  }

  set deliveryDate(date: string | undefined) {
    if (this.order) {
      this.order.dateExpected = date ? moment(date).toDate() : undefined
    }
  }

  public lookUpReminder(id: string) {
    if (!this.order) {
      return null
    }

    return this.reminders.get(id)
  }

  private async setReminders() {
    const reminderCollection = new ReminderCollection((await this.$firebase).firestore())
    const reminders = await reminderCollection.listRemindersByRestaurantCompanyId(this.userCompany.id)
    reminders.forEach(reminder => {
      if (
        this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId]
        && !this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId].includes(reminder.text)
      ) {
        this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId].push(reminder.text)
      } else if (!this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId]) {
        this.reminderMap[reminder.priceListEntryId + reminder.vendorCompanyId] = [reminder.text]
      }
    })
  };

  public async mounted() {
    this.isActive = this.userCompany.status === CompanyStatusEnum.ACTIVE

    if (this.$route.params.id === 'create') {
      this.action = 'create'

      if (this.$route.params.data) {
        this.order = this.createNewOrder()

        const parDataArray: {
          productName: string,
          category: string,
          parQuantity: number,
          onHandQuantity: number,
          priceListEntry: IVendorPriceListEntry,
          quantityType: string
        }[] = JSON.parse(this.$route.params.data)

        this.order.orderEntries = parDataArray.map(data => {
          const entry = new OrderEntry(data.priceListEntry.upc, data.priceListEntry.description)

          entry.quantity = Math.max(data.parQuantity - Math.max(data.onHandQuantity, 0), 0)
          entry.category = data.category
          entry.vendorCompanyId = data.priceListEntry.vendorCompanyId
          entry.priceListId = data.priceListEntry.priceListId
          entry.priceListEntryId = data.priceListEntry.id
          if (data.quantityType && data.quantityType.length) {
            entry.quantityType = data.quantityType
          }

          return entry
        })

        this.setOrderDraft(this.order)
      } else {
        this.order = this.createNewOrder()

        if (
          this.$route.query
          && this.$route.query.reorder
          && typeof this.$route.query.reorder === 'string'
        ) {
          const reOrder = await this.lookupExistingOrder(this.$route.query.reorder)
          if (reOrder && reOrder.orderEntries && this.order && this.order.orderEntries) {
            this.order.datePlaced = undefined

            this.order.orderEntries = reOrder.orderEntries.map(
              entry => Object.assign(
                new OrderEntry('', ''),
                entry,
                {
                  id: uuid(),
                  receivedQuantity: undefined,
                  receivedQuantityType: undefined,
                  additionalQuantity: undefined,
                  dateAccepted: undefined,
                  status: OrderEntryStatusEnum.PENDING,
                } as OrderEntry
              )
            )
          }
        } else {
          this.addOrderEntry()
        }
      }
    } else {
      this.action = 'modify'
      try {
        const order = await this.lookupExistingOrder(this.$route.params.id)

        if (order) {
          this.order = order
        }
      } catch (error) {
        const err = new Error('You do not have permissions to view that page')
        return store.dispatch(Actions.SET_ERROR, [err, this.$router])
      }
    }

    if (!this.order) {
      const err = new Error('You do not have permissions to view that page')
      return store.dispatch(Actions.SET_ERROR, [err, this.$router])
    }
    const companyService = await this.$companyService
    this.vendors = companyService.watchCompaniesByType(CompanyTypeEnum.VENDOR)
    await this.setReminders()

    await this.populatePriceListEntries()
  }

  public addVendor() {
    this.newVendor = {
      name: '',
      phoneNumber: '',
      email: ''
    }
    this.showAddVendorModal = true
  }

  public lookupVendorName(vendorCompanyId: string): string {
    const vendor = this.vendors.find((c: Company) => c.id === vendorCompanyId)
    return vendor ? vendor.displayName : ''
  }

  public addScanEntry(entry: OrderEntry) {
    if (!this.order) {
      return
    }

    this.removeEmptyEntries()
    this.order.orderEntries.unshift(entry)
  }

  public addOrderEntry() {
    if (!this.order) {
      return
    }

    const entry = new OrderEntry('', '')
    entry.quantity = 1
    this.order.orderEntries.unshift(entry)
  }

  public removeOrderEntry(entry: OrderEntry) {
    if (!this.order) {
      return
    }

    const index = this.order.orderEntries.indexOf(entry)
    if (index !== -1) {
      this.order.orderEntries.splice(index, 1)
    }
  }

  public isOrderValid(order: Order | null): order is Order {
    if (!(order instanceof Order)) {
      return false
    }

    if (!this.$refs.form.validate()) {
      if (!order.number) {
        this.errors.push('Please enter an order number')
      }
      if (!order.orderEntries || !order.orderEntries.length) {
        this.errors.push('You must enter at least one item')
        return false
      }
      if (!this.deliveryDate) {
        this.errors.push('Please enter the delivery date')
      }
      if (!order.orderEntries.every(entry => entry.quantity > 0)) {
        this.errors.push('Quantity must be greater than zero on every item')
      }
      if (
        !order.orderEntries.every(entry => entry.vendorCompanyId && entry.priceListId)
      ) {
        this.errors.push('You must select a vendor for every item')
      }
      if (!order.orderEntries.every(entry => entry.description)) {
        this.errors.push('You must enter a description for every item')
      }
      if (
        !order.orderEntries.every(entry =>
          entry.quantityType !== 'See order comments'
          || entry.orderingNotes
        )
      ) {
        this.errors.push('You must enter Ordering Comments that describe the Quantity Type for every item that has a Quantity Type of \'See order comments\'')
      }
    }

    if (!order.orderEntries.every(entry => entry.category)) {
      this.errors.push('Please select a category for every entry')
    }

    return !!this.$refs.form.validate() && !this.errors.length
  }

  public updateQuantity(entry: OrderEntry, quantity: number) {
    entry.quantity = quantity
  }

  public updateQuantityTypeSelection(quantityType: any) {
    this.selectedEntry.quantityType = quantityType
  }

  public async updateOrder() {
    if (this.isOrderValid(this.order)) {
      const collection = new OrderCollection((await this.$firebase).firestore())
      const saveOrder = {...this.order, status: OrderStatusEnum.CREATED} as Order
      await collection.save(saveOrder)
    }
  }

  public async save() {
    this.shouldSaveDraft = false
    this.errors = []
    this.removeEmptyEntries()
    await this.$nextTick()

    if (!this.isOrderValid(this.order)) {
      this.shouldSaveDraft = true
      return
    }
    this.isSaving = true

    await this.updateOrder()
    await this.$router.push({name: 'order-details', params: {id: this.order.id}})

    this.isSaving = false
  }

  public back() {
    if (this.order) {
      switch (this.action) {
        case 'create':
          this.$router.go(-1)
          break

        case 'modify':
          this.$router.push({name: 'order-details', params: {id: this.order.id}})
          break

        default:
          break
      }
    } else {
      this.$router.push({name: 'order-list'})
    }
  }

  public async loadVendors(newCompany?: any) {
    this.showAddVendorModal = false

    // refresh the vendor list
    this.populatePriceListEntries()

    if (newCompany && newCompany.id) {
      this.selectedEntry.vendorCompanyId = newCompany.id
      this.$refs.form.validate()
    }
  }

  @Watch('order', {deep: true})
  public orderChanges(order: Order | null, previousOrder: Order | null) {
    if (!this.shouldSaveDraft) {
      return
    }

    if (order && previousOrder) {
      this.processChange(order)
    }
  }

  public debounce(func: any, timeout = 300) {
    let timer: any
    return (...args: any[]) => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        func(...args)
      }, timeout)
    }
  }

  public processChange = this.debounce(async (order: Order) => await this.setOrderDraft(order))

  public async setOrderDraft(order: Order) {
    const orderCollection = new OrderCollection((await this.$firebase).firestore())
    const saveOrder = {...order, status: OrderStatusEnum.DRAFT} as Order

    await orderCollection.saveDraft(saveOrder)

    console.log('order draft saved: ', saveOrder.id)

    if (this.$route.path === '/orders/create/edit') {
      const nextURL = `${window.location.origin}/orders/${saveOrder.id}/edit`
      const nextTitle = 'Order Edit'
      const nextState = {additionalInformation: 'Updated the URL with JS'}

      // This will create a new entry in the browser's history, without reloading
      // window.history.pushState(nextState, nextTitle, nextURL);

      // This will replace the current entry in the browser's history, without reloading
      window.history.replaceState(nextState, nextTitle, nextURL)
    }
  }

  public async updateDetails(orderEntry: OrderEntry, priceListEntry: IVendorPriceListEntry) {
    if (!orderEntry) {
      Sentry.captureMessage('Order Entry is falsy', (scope) => {
        scope.setLevel(Severity.Warning)
        scope.setExtra(
          'extraDetails',
          {
            orderEntry,
            orderEntries: this.order && this.order.orderEntries,
            priceListEntry
          }
        )
        return scope
      })
    } else if (typeof orderEntry.updateVendorDetails !== 'function') {
      Sentry.captureMessage('Order Entry is not a function', (scope) => {
        scope.setLevel(Severity.Warning)
        scope.setExtra(
          'extraDetails',
          {
            orderEntry,
            orderEntries: this.order && this.order.orderEntries,
            priceListEntry
          }
        )
        return scope
      })
      const mockEntry = new OrderEntry('', '')
      mockEntry.updateVendorDetails.call(orderEntry, priceListEntry)
    } else {
      orderEntry.updateVendorDetails(priceListEntry)
    }
  }

  private createNewOrder() {
    const order = new Order(
      '',
      OrderTypeEnum.RESTAURANT,
      this.user.uid,
      this.userCompany.id,
      this.userCompany.name,
      this.userCompany.id
    )
    order.dateExpected = moment().add(1, 'day').toDate()

    return order
  }

  private removeEmptyEntries() {
    // filter out any blank order entries
    if (this.order && this.order.orderEntries && this.order.orderEntries.length > 0) {
      this.order.orderEntries = this.order.orderEntries.filter(entry => !entry.isBlank)
    }
  }

  private async lookupExistingOrder(id: string) {
    const collection = new OrderCollection((await this.$firebase).firestore())
    const order = await collection.get(id)
    if (!order || order.assignedCompanyId !== this.userCompany.id) {
      return null
    }

    return order
  }

  public async populatePriceListEntries() {
    const priceListCollection = new PriceListCollection((await this.$firebase).firestore())
    this.priceLists = await priceListCollection.listPriceListsByCompanyId(this.userCompany.id, this.userCompany.type, true)

    const masterCatalogCollection = new MasterCatalogCollection((await this.$firebase).firestore())

    this.priceListEntries = Array.prototype.concat(
      ...(await Promise.all(
        this.priceLists.map((priceList) =>
          masterCatalogCollection.getByCompanyId(priceList.vendorCompanyId).then((masterCatalog) =>
            [...priceList.entries, ...(masterCatalog ? masterCatalog.entries : [])].map((entry) => ({
              ...entry,
              priceListId: priceList.id,
              vendorCompanyId: priceList.vendorCompanyId,
            }))
          )
        )
      ))
    )
  }
}
